import { collection, doc, getDocs, getDoc, updateDoc, addDoc, query, where, orderBy, serverTimestamp, onSnapshot, Timestamp } from "firebase/firestore"
import { db } from "./config"

// Get all support chats
export const getAllSupportChats = async () => {
    try {
        const supportChatsRef = collection(db, "supportChats")
        const q = query(supportChatsRef, orderBy("lastMessageAt", "desc"))
        const querySnapshot = await getDocs(q)
        
        const chats = []
        querySnapshot.forEach((doc) => {
            chats.push({ id: doc.id, ...doc.data() })
        })
        
        return chats
    } catch (e) {
        console.error("Error getting support chats: ", e)
        return []
    }
}

// Real-time listener for all support chats
export const subscribeToSupportChats = (callback) => {
    try {
        const supportChatsRef = collection(db, "supportChats")
        const q = query(supportChatsRef, orderBy("lastMessageAt", "desc"))
        
        return onSnapshot(q, (querySnapshot) => {
            const chats = []
            querySnapshot.forEach((doc) => {
                chats.push({ id: doc.id, ...doc.data() })
            })
            callback(chats)
        }, (error) => {
            console.error("Error listening to support chats: ", error)
        })
    } catch (e) {
        console.error("Error setting up support chats listener: ", e)
    }
}

// Get support chats by status
export const getSupportChatsByStatus = async (status) => {
    try {
        const supportChatsRef = collection(db, "supportChats")
        const q = query(
            supportChatsRef, 
            where("status", "==", status),
            orderBy("lastMessageAt", "desc")
        )
        const querySnapshot = await getDocs(q)
        
        const chats = []
        querySnapshot.forEach((doc) => {
            chats.push({ id: doc.id, ...doc.data() })
        })
        
        return chats
    } catch (e) {
        console.error("Error getting support chats by status: ", e)
        return []
    }
}

// Real-time listener for support chats by status
export const subscribeToSupportChatsByStatus = (status, callback) => {
    try {
        const supportChatsRef = collection(db, "supportChats")
        const q = query(
            supportChatsRef, 
            where("status", "==", status),
            orderBy("lastMessageAt", "desc")
        )
        
        return onSnapshot(q, (querySnapshot) => {
            const chats = []
            querySnapshot.forEach((doc) => {
                chats.push({ id: doc.id, ...doc.data() })
            })
            callback(chats)
        }, (error) => {
            console.error("Error listening to support chats by status: ", error)
        })
    } catch (e) {
        console.error("Error setting up support chats by status listener: ", e)
    }
}

// Update chat status
export const updateChatStatus = async (chatId, status) => {
    try {
        const chatRef = doc(db, "supportChats", chatId)
        await updateDoc(chatRef, { status })
    } catch (e) {
        console.error("Error updating chat status: ", e)
    }
}

// Assign chat to agent
export const assignChatToAgent = async (chatId, agentId, agentName) => {
    try {
        const chatRef = doc(db, "supportChats", chatId)
        await updateDoc(chatRef, { 
            assignedTo: agentId,
            assignedToName: agentName
        })
    } catch (e) {
        console.error("Error assigning chat to agent: ", e)
    }
}

// Add admin message to chat
export const addAdminMessage = async (chatId, message, adminId, adminName) => {
    try {
        console.log('Adding admin message:', { chatId, message, adminId, adminName })
        const chatRef = doc(db, "supportChats", chatId)
        const messageId = Date.now().toString()
        
        const newMessage = {
            id: messageId,
            message: message,
            senderId: adminId,
            senderName: adminName,
            senderType: "admin",
            timestamp: Timestamp.now(),
            isRead: false
        }
        
        console.log('New message object:', newMessage)
        
        // Get current chat data
        const chatDoc = await getDoc(chatRef)
        const chatData = chatDoc.data()
        
        console.log('Current chat data:', chatData)
        console.log('Current messages:', chatData?.messages)
        
        if (chatData) {
            const updatedMessages = [...(chatData.messages || []), newMessage]
            console.log('Updated messages array:', updatedMessages)
            
            await updateDoc(chatRef, {
                messages: updatedMessages,
                lastMessageAt: serverTimestamp()
            })
            
            console.log('Message saved successfully to Firebase')
        } else {
            console.log('No chat data found')
        }
    } catch (e) {
        console.error("Error adding admin message: ", e)
        throw e
    }
}

// Mark messages as read
export const markMessagesAsRead = async (chatId) => {
    try {
        const chatRef = doc(db, "supportChats", chatId)
        const chatDoc = await getDoc(chatRef)
        const chatData = chatDoc.data()
        
        if (chatData && chatData.messages) {
            const updatedMessages = chatData.messages.map(msg => ({
                ...msg,
                isRead: true
            }))
            
            await updateDoc(chatRef, {
                messages: updatedMessages
            })
        }
    } catch (e) {
        console.error("Error marking messages as read: ", e)
        throw e
    }
}

// Update chat priority
export const updateChatPriority = async (chatId, priority) => {
    try {
        const chatRef = doc(db, "supportChats", chatId)
        await updateDoc(chatRef, { priority })
    } catch (e) {
        console.error("Error updating chat priority: ", e)
    }
}

// Real-time listener for a specific chat
export const subscribeToChat = (chatId, callback) => {
    try {
        const chatRef = doc(db, "supportChats", chatId)
        
        return onSnapshot(chatRef, (doc) => {
            if (doc.exists()) {
                const chatData = { id: doc.id, ...doc.data() }
                console.log('Firebase real-time update:', chatData.id)
                console.log('Messages in real-time update:', chatData.messages?.length)
                callback(chatData)
            }
        }, (error) => {
            console.error("Error listening to chat: ", error)
        })
    } catch (e) {
        console.error("Error setting up chat listener: ", e)
    }
} 